import React, { useState } from 'react'
import PersonSearch from './PersonSearch'
import { Link, useForm, usePage } from '@inertiajs/inertia-react'
import { Inertia } from '@inertiajs/inertia'
import AlertDismissible from '../../../Shared/Components/AlertDismissible'
import Breadcrumbs from '../../../Shared/Components/Breadcrumbs'
import Layout from '../../../Shared/Layout/LayoutAdmin'
import PaginationList from '../../../Shared/Components/PaginationList'
import Row from 'react-bootstrap/Row'
import Form from 'react-bootstrap/Form'
import Button from 'react-bootstrap/Button'
import Modal from 'react-bootstrap/Modal'
import OverlayTrigger from 'react-bootstrap/OverlayTrigger'
import Tooltip from 'react-bootstrap/Tooltip'
import { SIA_NAVY } from '../../../constants/siaTheme'

/** Cursor «ojo» para celdas con texto extra (fallback zoom-in si el navegador no aplica el SVG). */
const OBS_CURSOR_EYE =
    'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'22\' height=\'22\' viewBox=\'0 0 24 24\' fill=\'none\' stroke=\'%23292d32\' stroke-width=\'2\' stroke-linecap=\'round\' stroke-linejoin=\'round\'%3E%3Cpath d=\'M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z\'/%3E%3Ccircle cx=\'12\' cy=\'12\' r=\'3\'/%3E%3C/svg%3E") 11 11, zoom-in'

function formatDate(val) {
    if (!val) return ''
    const d = String(val).slice(0, 10)
    const [y, m, day] = d.split('-')
    if (!y || !m || !day) return val
    return `${day}/${m}/${y}`
}

function adviserName(user) {
    if (!user) return '—'
    const person = user.adviser || user.employee
    return [user.name, person?.lastname_ap, person?.lastname_am].filter(Boolean).join(' ')
}

function fullName(row) {
    return [row.nombre, row.apellido_paterno, row.apellido_materno].filter(Boolean).join(' ')
}

const ProspectLogIndex = ({ entries, filters, mode, advisers }) => {
    const { flash } = usePage().props
    const manage = mode === 'manage'
    const [deleteTarget, setDeleteTarget] = useState(null)
    const [deleting, setDeleting] = useState(false)

    const filterForm = useForm({
        search: filters.search || '',
        user_id: filters.user_id || '',
    })

    const indexRoute = 'prospect-log.index'
    const pathPrefix = 'admin/bitacora-prospectos'

    const submitFilters = (e) => {
        e.preventDefault()
        filterForm.get(route(indexRoute), { preserveState: true })
    }

    const clearFilters = () => {
        filterForm.setData({ search: '', user_id: '' })
        filterForm.get(route(indexRoute))
    }

    const paginationFilters = {
        search: filters.search || undefined,
        user_id: filters.user_id || undefined,
    }

    const confirmDelete = () => {
        if (!deleteTarget) return
        setDeleting(true)
        Inertia.delete(route('prospect-log.destroy', deleteTarget.id), {
            preserveScroll: true,
            onFinish: () => {
                setDeleting(false)
                setDeleteTarget(null)
            },
        })
    }

    const data = entries.data || []

    const editHref = (row) => route('prospect-log.edit', row.id)

    const createHref = route('prospect-log.create')

    return (
        <Row>
            {flash.success && <AlertDismissible message={flash.success} type="success" />}
            <span className="pb-7 pt-7">
                <Breadcrumbs currentPath={pathPrefix} />
            </span>

            <div
                className="rounded-2 mb-8 py-4 px-4 text-center text-white text-uppercase fw-bold fs-4"
                style={{ backgroundColor: SIA_NAVY, letterSpacing: '0.04em' }}
            >
                Información sobre los prospectos
            </div>

            <div className="card card-flush h-md-100">
                <div className="card-header pt-7 flex-wrap gap-3">
                    <h3 className="card-title align-items-start flex-column">
                        <span className="card-label fw-bold text-gray-800">Bitácora de Prospectos</span>
                    </h3>
                    <div className="card-toolbar">
                        <Link href={createHref} className="btn btn-sm btn-primary">
                            Nuevo prospecto
                        </Link>
                    </div>
                </div>

                <div className="card-body pt-6">
                    <Form onSubmit={submitFilters} className="d-flex flex-wrap align-items-end gap-3 mb-8">
                        <div className="flex-grow-1" style={{ minWidth: '220px', maxWidth: '420px' }}>
                            <label className="form-label fs-7 fw-bold text-gray-600 mb-2">Buscar</label>
                            <Form.Control
                                type="search"
                                className="form-control form-control-sm form-control-solid"
                                placeholder="Nombre, teléfono, correo, escuela…"
                                value={filterForm.data.search}
                                onChange={(e) => filterForm.setData('search', e.target.value)}
                            />
                        </div>
                        {manage && advisers && advisers.length > 0 && (
                            <div style={{ minWidth: '200px' }}>
                                <label className="form-label fs-7 fw-bold text-gray-600 mb-2">Asesor</label>
                                <PersonSearch
                                    options={advisers}
                                    value={filterForm.data.user_id}
                                    placeholder="Buscar agente o empleado"
                                    onChange={(id) => filterForm.setData('user_id', id)}
                                />
                            </div>
                        )}
                        <Button type="submit" size="sm" variant="primary" disabled={filterForm.processing}>
                            Buscar
                        </Button>
                        <Button type="button" size="sm" variant="light" onClick={clearFilters}>
                            Limpiar
                        </Button>
                    </Form>

                    <div className="table-responsive">
                        <table className="table table-row-bordered table-row-gray-300 align-middle gs-0 gy-4">
                            <thead>
                                <tr className="fw-bold text-muted text-uppercase fs-7 bg-light">
                                    {manage && <th className="min-w-150px ps-4 rounded-start">Asesor</th>}
                                    <th className={manage ? 'min-w-100px' : 'min-w-100px ps-4 rounded-start'}>Fecha</th>
                                    <th className="min-w-200px">Nombre</th>
                                    <th className="min-w-110px">Teléfono</th>
                                    <th className="min-w-180px">Correo</th>
                                    <th className="min-w-140px">Escuela</th>
                                    <th className="min-w-140px">Municipio</th>
                                    <th className="min-w-220px">Observaciones</th>
                                    <th className="min-w-120px text-end pe-4 rounded-end">Acciones</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.length === 0 && (
                                    <tr>
                                        <td className="text-center py-10 text-gray-700" colSpan={manage ? 9 : 8}>
                                            No hay registros.
                                        </td>
                                    </tr>
                                )}
                                {data.map((row) => (
                                    <tr key={row.id}>
                                        {manage && (
                                            <td className="ps-4">
                                                <span className="text-gray-800 fw-semibold">{adviserName(row.user)}</span>
                                            </td>
                                        )}
                                        <td className={manage ? '' : 'ps-4'}>
                                            <span className="text-gray-800">{formatDate(row.fecha)}</span>
                                        </td>
                                        <td>
                                            <span className="text-gray-800 fw-semibold text-uppercase">{fullName(row)}</span>
                                        </td>
                                        <td className="text-gray-700">{row.telefono}</td>
                                        <td className="text-gray-700 text-uppercase">{row.email}</td>
                                        <td className="text-gray-700">{row.nombre_escuela}</td>
                                        <td className="text-gray-700">{row.municipio}</td>
                                        <td>
                                            {row.observaciones ? (
                                                <OverlayTrigger
                                                    placement="top"
                                                    overlay={
                                                        <Tooltip id={`obs-tip-${row.id}`} className="text-start">
                                                            <span style={{ whiteSpace: 'pre-wrap' }}>{row.observaciones}</span>
                                                        </Tooltip>
                                                    }
                                                >
                                                    <span
                                                        className="text-gray-600 text-truncate d-inline-block border-bottom border-dashed border-gray-400"
                                                        style={{ maxWidth: '260px', cursor: OBS_CURSOR_EYE }}
                                                        role="button"
                                                        tabIndex={0}
                                                    >
                                                        {row.observaciones}
                                                    </span>
                                                </OverlayTrigger>
                                            ) : (
                                                <span className="text-muted">—</span>
                                            )}
                                        </td>
                                        <td className="text-end pe-4">
                                            <Link href={editHref(row)} className="btn btn-sm btn-light-primary me-2">
                                                Editar
                                            </Link>
                                            {manage && (
                                                <button
                                                    type="button"
                                                    className="btn btn-sm btn-light-danger"
                                                    onClick={() => setDeleteTarget(row)}
                                                >
                                                    Eliminar
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    <div className="d-flex justify-content-center py-6">
                        <PaginationList links={entries.links} filters={paginationFilters} />
                    </div>
                </div>
            </div>

            {manage && (
                <Modal show={deleteTarget !== null} onHide={() => !deleting && setDeleteTarget(null)} centered>
                    <Modal.Header closeButton={!deleting}>
                        <Modal.Title>Confirmar eliminación</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        ¿Eliminar el registro de <strong>{deleteTarget ? fullName(deleteTarget) : ''}</strong>? Esta acción no se puede deshacer.
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="light" disabled={deleting} onClick={() => setDeleteTarget(null)}>
                            Cancelar
                        </Button>
                        <Button variant="danger" disabled={deleting} onClick={confirmDelete}>
                            {deleting ? 'Eliminando…' : 'Eliminar'}
                        </Button>
                    </Modal.Footer>
                </Modal>
            )}
        </Row>
    )
}

ProspectLogIndex.layout = (page) => <Layout title="Bitácora de prospectos">{page}</Layout>

export default ProspectLogIndex
